import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Receipt, Plus, X, Loader2, Paperclip, Info, Landmark, RefreshCw, Trash2, Pencil, FileText, ExternalLink, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_STYLE = {
  approved: { cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2, label: "Approved" },
  declined: { cls: "bg-rose-50 text-rose-700", icon: XCircle, label: "Declined" },
  submitted: { cls: "bg-amber-50 text-amber-700", icon: Clock, label: "Pending" },
  reimbursed: { cls: "bg-indigo-50 text-indigo-700", icon: CheckCircle2, label: "Reimbursed" },
};

function StatusCell({ e, onSet, canApprove }) {
  const s = STATUS_STYLE[e.status || "submitted"] || STATUS_STYLE.submitted;
  const Icon = s.icon;
  const st = e.status || "submitted";
  const by = e.status === "reimbursed" ? `Reimbursed by ${e.reimbursed_by} ${fmtDate(e.reimbursed_at)}` : e.reviewed_by ? `${s.label} by ${e.reviewed_by} ${fmtDate(e.reviewed_at)}` : "";
  return (
    <div className="flex items-center gap-2" data-testid={`expense-status-${e.expense_id}`} title={by}>
      <span className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-2 py-0.5 ${s.cls}`}><Icon className="w-3 h-3" /> {s.label}</span>
      {canApprove && st === "submitted" && <button onClick={() => onSet(e.expense_id, "approved")} data-testid={`expense-approve-${e.expense_id}`} className="text-slate-300 hover:text-emerald-600" title="Approve"><CheckCircle2 className="w-4 h-4" /></button>}
      {canApprove && (st === "submitted" || st === "approved") && <button onClick={() => onSet(e.expense_id, "declined")} data-testid={`expense-decline-${e.expense_id}`} className="text-slate-300 hover:text-rose-600" title="Decline"><XCircle className="w-4 h-4" /></button>}
      {canApprove && st === "approved" && <button onClick={() => onSet(e.expense_id, "reimbursed")} data-testid={`expense-reimburse-${e.expense_id}`} className="text-xs font-semibold text-indigo-600 hover:underline">Mark reimbursed</button>}
    </div>
  );
}

const LOCKED = new Set(["approved", "reimbursed"]);
const money = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const VAT_RATES = { Auto: 0.2, "20%": 0.2, "5%": 0.05, "0%": 0, Exempt: 0 };
const receiptUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/expenses/receipt/${path}?auth=${encodeURIComponent(localStorage.getItem("htss_token") || "")}`;

function Lbl({ children, req }) {
  return <label className="block text-sm font-semibold text-slate-700 mb-1">{children}{req && <span className="text-rose-500 ml-0.5">*</span>}</label>;
}
const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-indigo-500" />}{title}</h3>
        {right}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

const EMPTY = {
  claimant_id: "", type: "Payment", category: "", dated: new Date().toISOString().slice(0, 10),
  currency: "Pounds Sterling", total_value: "", vat: "Auto", description: "", receipt_reference: "",
  project_id: "", task_legacy_id: "", recurs: "Does Not Recur", attachment_description: "",
};

export function ExpenseForm({ expense, onClose, onSaved }) {
  const editing = !!expense;
  const [opts, setOpts] = useState({ claimants: [], categories: [], projects: [] });
  const [f, setF] = useState(editing ? { ...EMPTY, ...expense, total_value: expense.total_value ?? "" } : EMPTY);
  const [receipt, setReceipt] = useState(expense?.receipt_file || null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { api.get("/expenses/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const { user, can } = useAuth();
  const pickClaimant = can("expenses.manage");
  useEffect(() => {
    if (editing || !user) { return; }
    const me = opts.claimants.find((c) => c.id === user.id || (c.email && c.email === user.email));
    if (me) { setF((s) => (s.claimant_id ? s : { ...s, claimant_id: me.id })); }
  }, [opts.claimants, user, editing]);

  const claimantName = () => (opts.claimants.find((c) => c.id === f.claimant_id) || {}).name || f.claimant_name || "";
  const gross = parseFloat(f.total_value) || 0;
  const rate = VAT_RATES[f.vat] ?? 0.2;
  const net = rate ? gross / (1 + rate) : gross;
  const vatAmt = gross - net;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/expenses/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceipt(r.data);
      const o = r.data.ocr;
      if (o) {
        setF((s) => ({
          ...s,
          total_value: o.total != null ? o.total : s.total_value,
          dated: o.date || s.dated,
          receipt_reference: o.supplier || s.receipt_reference,
          description: s.description || o.supplier || "",
        }));
        toast.success("Smart Capture filled the amount, date & supplier — please check.");
      } else {
        toast.success("Receipt attached");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (addAnother) => {
    if (!f.category) { toast.error("Category is required"); return; }
    if (!f.description) { toast.error("Description is required"); return; }
    setBusy(true);
    const payload = { ...f, claimant_name: claimantName(), total_value: gross, project_id: f.project_id || null, task_legacy_id: f.task_legacy_id || null, receipt_file: receipt || null };
    try {
      if (editing) await api.patch(`/expenses/${expense.expense_id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Expense updated" : "Expense created");
      onSaved();
      if (editing || !addAnother) onClose();
      else { setF((s) => ({ ...EMPTY, claimant_id: s.claimant_id, category: s.category, currency: s.currency })); setReceipt(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save expense");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="expense-form-overlay">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-2xl bg-slate-50 shadow-2xl flex flex-col animate-in slide-in-from-right" data-testid="expense-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <h2 className="text-lg font-bold text-slate-900">{editing ? "Edit expense" : "New Out-of-Pocket Expense"}{claimantName() ? ` — ${claimantName()}` : ""}</h2>
          <button onClick={onClose} aria-label="Close" data-testid="expense-form-close" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <Section icon={Paperclip} title="Attachment">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="rounded-lg border border-slate-200 bg-white px-3 h-10 inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:border-indigo-300 hover:text-indigo-600 cursor-pointer" data-testid="expense-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {uploading ? "Reading receipt…" : "Upload a file"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="expense-file-input" />
              </label>
              {receipt && (
                <a href={receiptUrl(receipt.path)} target="_blank" rel="noreferrer" data-testid="expense-receipt-link" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline">
                  <FileText className="w-4 h-4" /> {receipt.filename} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            <p className="text-[11px] text-slate-400 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Smart Capture reads the amount, date &amp; supplier from an uploaded photo — always double-check.</p>
            <div><Lbl>Attachment description</Lbl>
              <input value={f.attachment_description} onChange={(e) => set("attachment_description", e.target.value)} className={inputCls} data-testid="expense-attachment-desc" placeholder="e.g. Car rental receipt" /></div>
          </Section>

          <Section icon={Receipt} title="Expense details" right={<span className="text-xs text-slate-400">Required fields <span className="text-rose-500">*</span></span>}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Lbl>Claimant</Lbl>
                {pickClaimant ? (
                  <select value={f.claimant_id} onChange={(e) => set("claimant_id", e.target.value)} className={inputCls} data-testid="expense-claimant">
                    <option value="">— Select —</option>
                    {opts.claimants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : <div className={`${inputCls} flex items-center bg-slate-100 text-slate-600`} data-testid="expense-claimant-self">{claimantName() || user?.name || user?.email}</div>}</div>
              <div><Lbl>Type</Lbl>
                <select value={f.type} onChange={(e) => set("type", e.target.value)} className={inputCls} data-testid="expense-type"><option>Payment</option><option>Receipt</option></select></div>
              <div><Lbl req>Category</Lbl>
                <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="expense-category">
                  <option value="">— Select —</option>
                  {opts.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><Lbl req>Dated</Lbl>
                <input type="date" value={f.dated} onChange={(e) => set("dated", e.target.value)} className={inputCls} data-testid="expense-dated" /></div>
              <div><Lbl req>Currency</Lbl>
                <select value={f.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls} data-testid="expense-currency"><option>Pounds Sterling</option><option>Euro</option><option>US Dollar</option></select></div>
              <div><Lbl req>Total value (inc. VAT)</Lbl>
                <div className="flex gap-2">
                  <input type="number" step="0.01" value={f.total_value} onChange={(e) => set("total_value", e.target.value)} className={inputCls} data-testid="expense-total" placeholder="0.00" />
                  <select value={f.vat} onChange={(e) => set("vat", e.target.value)} className={`${inputCls} w-28`} data-testid="expense-vat" title="VAT"><option>Auto</option><option>20%</option><option>5%</option><option>0%</option><option>Exempt</option></select>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="expense-vat-breakdown">
              {[["Net", net], ["VAT", vatAmt], ["Gross", gross]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div>
                  <div className="text-sm font-bold text-slate-800 tabular-nums">{money(v)}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400"><Info className="inline w-3 h-3 mr-1" />Total value is VAT-inclusive. <em>Auto</em> uses the standard 20% rate.</p>
            <div><Lbl req>Description</Lbl>
              <input value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="expense-description" /></div>
            <div><Lbl>Receipt reference</Lbl>
              <input value={f.receipt_reference} onChange={(e) => set("receipt_reference", e.target.value)} className={inputCls} data-testid="expense-receipt-ref" placeholder="e.g. Car Rental" /></div>
          </Section>

          <Section icon={Landmark} title="Is this a project expense?">
            <div><Lbl>Link to project</Lbl>
              <select value={f.project_id} onChange={(e) => { set("project_id", e.target.value); set("task_legacy_id", ""); }} className={inputCls} data-testid="expense-project">
                <option value="">— None —</option>
                {opts.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            {f.project_id && (
              <div className="mt-3"><Lbl>Link to a job in this project (optional)</Lbl>
                <select value={f.task_legacy_id} onChange={(e) => set("task_legacy_id", e.target.value)} className={inputCls} data-testid="expense-job">
                  <option value="">— Whole project —</option>
                  {(opts.projects.find((p) => p.id === f.project_id)?.tasks || []).map((t) => (
                    <option key={t.legacy_id} value={t.legacy_id}>{t.legacy_id} — {t.title}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">When approved, this expense is added to that job's Costs and reduces its Gross Profit.</p>
              </div>
            )}
          </Section>

          <Section icon={RefreshCw} title="Recurring options">
            <div><Lbl>This expense recurs</Lbl>
              <select value={f.recurs} onChange={(e) => set("recurs", e.target.value)} className={inputCls} data-testid="expense-recurs"><option>Does Not Recur</option><option>Weekly</option><option>Monthly</option><option>Quarterly</option><option>Yearly</option></select></div>
          </Section>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center gap-3">
          <button onClick={() => submit(false)} disabled={busy} data-testid="expense-save" className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 h-10 inline-flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{editing ? "Save changes" : "Create new expense"}</button>
          {!editing && <button onClick={() => submit(true)} disabled={busy} data-testid="expense-create-another" className="rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold px-5 h-10">Create and add another</button>}
          <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-700 ml-auto" data-testid="expense-cancel">Cancel</button>
        </div>
      </aside>
    </div>
  );
}

export default function ExpensesPage() {
  const { can } = useAuth();
  const canManage = can("expenses.manage");
  const canApprove = can("expenses.approve");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(() => {
    api.get("/expenses").then((r) => setData(r.data)).catch(() => setData({ expenses: [], total: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const setStatus = async (id, status) => {
    let reference = null;
    let note = null;
    if (status === "reimbursed") { reference = window.prompt("Payment reference for this reimbursement (optional)"); if (reference === null) { return; } }
    if (status === "declined") { note = window.prompt("Reason for declining (shown to the claimant)"); if (note === null) { return; } }
    try { await api.post(`/expenses/${id}/status`, { status, reference, note: note || null }); toast.success(`Expense ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  const rows = data?.expenses || [];
  const hl = new URLSearchParams(window.location.search).get("expense");
  const closeForm = () => { setOpen(false); setEditRow(null); };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-expenses">
      <div className="flex items-center gap-3 mb-8">
        <PageIcon />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Expenses</h1>
          <p className="text-sm text-slate-500 mt-0.5">Out-of-pocket expense claims{data ? ` · ${money(data.total)} total` : ""}</p>
        </div>
        {canManage && <button onClick={() => { setEditRow(null); setOpen(true); }} data-testid="expense-new-btn" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 h-10 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New expense</button>}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {data === null ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center" data-testid="expenses-empty">
            <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No expenses yet. Click <span className="font-medium text-slate-600">New expense</span> to add one.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Claimant</th><th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Description</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3 text-right">VAT</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.expense_id} className={`border-b border-slate-50 hover:bg-slate-50/60 ${hl === e.expense_id ? "bg-amber-50 ring-2 ring-inset ring-amber-300" : ""}`} data-testid={`expense-row-${e.expense_id}`}>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(e.dated)}</td>
                  <td className="px-4 py-3 text-slate-800">{e.claimant_name || "—"}</td>
                  <td className="px-4 py-3"><span className="inline-block rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0.5">{e.category}</span></td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                    {e.receipt_file && <a href={receiptUrl(e.receipt_file.path)} target="_blank" rel="noreferrer" className="text-indigo-500 hover:text-indigo-700 mr-1" title="View receipt"><Paperclip className="inline w-3.5 h-3.5" /></a>}
                    {e.description}{e.receipt_reference ? ` · ${e.receipt_reference}` : ""}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500 tabular-nums whitespace-nowrap">{money(e.net ?? e.total_value)}</td>
                  <td className="px-4 py-3 text-right text-slate-500 tabular-nums whitespace-nowrap">{money(e.vat_amount)}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-900 tabular-nums whitespace-nowrap">{money(e.gross ?? e.total_value)}</td>
                  <td className="px-4 py-3"><StatusCell e={e} onSet={setStatus} canApprove={canApprove} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canManage && !LOCKED.has(e.status) && <button onClick={() => { setEditRow(e); setOpen(true); }} data-testid={`expense-edit-${e.expense_id}`} className="text-slate-300 hover:text-indigo-600 mr-2" aria-label="Edit"><Pencil className="w-4 h-4" /></button>}
                    {canManage && !LOCKED.has(e.status) && <button onClick={() => del(e.expense_id)} data-testid={`expense-delete-${e.expense_id}`} className="text-slate-300 hover:text-rose-600" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && <ExpenseForm expense={editRow} onClose={closeForm} onSaved={load} />}
    </div>
  );
}
